import React, { ComponentType, ForwardedRef } from 'react';
import type { AccessibilityProps } from 'react-native';

import type { SxStyleProp } from '@rover/kibble/core/styledSystemPropTypes';
import { HybridOnPressHandler } from '@rover/kibble/core/utils';

import { ThemeSpacing, ThemeTextSizes } from '../../styles';
import {
  FlexboxSystemProps,
  LayoutSystemProps,
  MarginSystemProps,
  PaddingSystemProps,
  SizeSystemType,
  StringSystemType,
} from '../styledSystemPropTypes';

/** Button size options */
export type ButtonSizes = 'small' | 'default' | 'large';
/** Button visual style variants */
export type ButtonVariants = 'default' | 'primary' | 'flat' | 'link';
/** Generic type mapping button sizes to values */
export type ButtonSizeMap<T> = {
  small: T;
  default: T;
  large: T;
};

// Height of the button is: text line height + 2*vertical padding + 4px total border width
// We have to use some non-theme values here to get proper button heights
export type ButtonYPaddings = '6px' | '10px' | '18px';
export const ButtonYPaddingMap: ButtonSizeMap<ButtonYPaddings> = {
  small: '6px',
  default: '10px',
  large: '18px',
};

export const ButtonXPaddingMap: ButtonSizeMap<ThemeSpacing> = {
  small: '4x',
  default: '6x',
  large: '8x',
};

export const ButtonSizeTextSizeMap: ButtonSizeMap<ThemeTextSizes> = {
  small: '100',
  default: '200',
  large: '300',
};

export const ButtonIconSizeMap: ButtonSizeMap<string> = {
  small: '16px',
  default: '24px',
  large: '24px',
};

export const ButtonIconMarginMap: ButtonSizeMap<ThemeSpacing> = {
  small: '2x',
  default: '3x',
  large: '4x',
};

type InternalSubVariants = 'base' | 'contrast' | 'destructive' | 'contrastDestructive';
type InternalVariantWithSubVariant = `${ButtonVariants}.${InternalSubVariants}`;
type InternalVariantWithSubVariantWithPressed = `${InternalVariantWithSubVariant}_pressed`;

export type InternalButtonVariants =
  | 'disabled'
  | 'link_disabled'
  | InternalVariantWithSubVariant
  | InternalVariantWithSubVariantWithPressed;

/** For now, this is only props that are available on web.  Ignored on React Native. */
export type ButtonWebOnlyProps = {
  /** ARIA label for accessibility when button has no visible text https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label */
  'aria-label'?: string;
  /** QA automation identifier */
  'data-qa-id'?: string;
  /** Tab order index for keyboard navigation */
  tabIndex?: number;
  /** Button type attribute (affects form submission), defaults to 'button' */
  type?: 'button' | 'submit' | 'reset';
  /** AVOID USING, supports a legacy use case in UnwrappedFileUploadButton */
  className?: string;
  /** Added to support some edge cases on web */
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  /** Added to support some edge cases on web */
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  /**  HTML id prop */
  id?: string;
  /** HTML form prop for associating the button with a form https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/form */
  form?: string;
  /** When href is passed, this sets the <a> tag rel attribute */
  rel?: string;
  /** <a> tag target attribute, only works if href is set */
  hrefTarget?: '_blank' | '_self' | '_parent' | '_top';
  /** React ref for the underlying button element */
  ref?: ForwardedRef<HTMLElement>;
};

/** For now, this is only props that are available on RxN.  Ignored on Web. */
export type ButtonRxnOnlyProps = Omit<AccessibilityProps, 'role'> & {
  /** PREFER USING THE SX PROP. Additional inline style object, allowed on RxN for legacy support reasons */
  style?: Record<string, string | number>;
  /** React ref for the underlying button element */
  ref?: ForwardedRef<React.ElementType>;
  /** For use with link variant buttons that are inlined with text, allows vertical adjustment */
  inlineLinkVerticalAdjustment?: string | number;
};

/** Base props without the platform-specific nested props */
type BaseButtonProps = MarginSystemProps &
  PaddingSystemProps &
  LayoutSystemProps &
  FlexboxSystemProps & {
    /** Align button within its container */
    alignSelf?: StringSystemType;
    /** Button text content */
    children?: React.ReactNode;
    /** Custom gap between button children, overrides default spacing */
    childrenFlexGap?: string;
    /** Make button circular (equal padding on all sides) */
    circular?: boolean;
    /** Use contrast colors for button on dark backgrounds */
    contrast?: boolean;
    /** Apply destructive/warning styling (typically red) */
    destructive?: boolean;
    /** Disable button interactions */
    disabled?: boolean;
    /** Make button fill full width of container */
    fullWidth?: boolean;
    /** URL to navigate to when button is clicked, turns button into a link */
    href?: string;
    /** Icon component to display alongside button text */
    icon?: ComponentType<any>;
    /** Position of icon relative to text */
    iconAlignment?: 'left' | 'right';
    /** Show button in selected state, requires ARIA state props */
    isSelected?: boolean;
    /** Show loading spinner */
    loading?: boolean;
    /** Hybrid onPress handler with both RxN and web event args - use this if you need access to the event object */
    onPress?: HybridOnPressHandler;
    /** @deprecated Use onPress instead for better event handling. Simple click handler with zero args */
    onClick?: () => void;
    /** Alternative icon to show when isSelected is true */
    selectedIcon?: ComponentType<any>;
    /** Button size preset */
    size?: ButtonSizes;
    /** ID for finding this element in unit tests, renders as `data-testid` on Web and `testID` on React Native */
    testID?: string;
    /** Visual style variant */
    variant?: ButtonVariants;
    /** Button width */
    width?: SizeSystemType;
    /** Horizontal alignment of button content */
    justifyContent?: StringSystemType;
    /** Theme-aware custom style overrides */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;
    /** AVOID USING, supports some questionable edge cases */
    roleOverride?: 'button' | 'link';
    /** AVOID USING, custom font weight override */
    fontWeight?: string;

    /** @deprecated use hrefTarget instead */
    target?: never;
    /** DO NOT USE, Override the underlying element type */
    as?: never;
    /** DO NOT USE, use href instead */
    ref?: never;
  };

export type ButtonCommonProps = BaseButtonProps & {
  /** Web-only props and web-specific overrides, ignored on react native */
  webProps?: Partial<Omit<BaseButtonProps, keyof ButtonWebOnlyProps>> & ButtonWebOnlyProps;
  /** RxN-only props and rxn-specific overrides, ignored on web */
  rxnProps?: Partial<Omit<BaseButtonProps, keyof ButtonRxnOnlyProps>> & ButtonRxnOnlyProps;
};

export function getVariant(
  disabled: boolean,
  destructive: boolean,
  contrast: boolean,
  variant: ButtonVariants = 'default',
  // only used on native to provide press styles
  isPressed: boolean = false
): InternalButtonVariants {
  // disabled styles have top priority and no pressed state
  if (disabled) return variant === 'link' ? 'link_disabled' : 'disabled';

  let resolvedVariant: InternalVariantWithSubVariant = `${variant}.base`;
  if (contrast && destructive) {
    resolvedVariant = `${variant}.contrastDestructive`;
  } else if (contrast) {
    resolvedVariant = `${variant}.contrast`;
  } else if (destructive) {
    resolvedVariant = `${variant}.destructive`;
  }

  // if pressed, append `_pressed` to the resolved variant.  Should only happen on native!
  return isPressed ? `${resolvedVariant}_pressed` : resolvedVariant;
}

export function getXPadding(
  size: ButtonSizes,
  circular: boolean,
  variant: ButtonVariants = 'default'
): ThemeSpacing | ButtonYPaddings | '0px' {
  if (variant === 'link') return '0px';
  return circular ? ButtonYPaddingMap[size] : ButtonXPaddingMap[size];
}

export function getYPadding(
  size: ButtonSizes,
  variant: ButtonVariants = 'default'
): ButtonYPaddings | '0px' {
  if (variant === 'link') return '0px';
  return ButtonYPaddingMap[size];
}

export function getFullWidthPropForContainer(
  fullWidth: boolean,
  variant: ButtonVariants = 'default'
):
  | {
      width: '100%';
    }
  | {
      alignSelf: 'center';
    }
  | Record<string, never> {
  // link variant is inline, fullWidth/alignSelf don't apply
  if (variant === 'link') return {};
  return fullWidth
    ? {
        width: '100%',
      }
    : {
        alignSelf: 'center',
      };
}
